import * as React from 'react';
import { useCallback, useRef } from 'react';

import { AsyncContent } from '~/components/AsyncContent';
import { AdminWorkspaceRoles } from '~/data/user/role';
import { useRefDimensions } from '~/hooks/useRefDimensions';
import {
  MarkTaskActions,
  WorklistPanels,
  WorklistRowMode,
} from '~/scenes/MyWorkspace/components/Widgets/components/MyWorklistsWidget/components/common';
import { CollaborationReadApiClient, People } from '~/services/Http';
import { BulkUpdateWorklistTasksStatusInputDto } from '~/services/Http/client.people.generated';

import { useSxStyles } from './useStyles';

type Props = {
  isLoading: boolean;
  failedToLoad: boolean;
  worklistData: People.WorklistDto[];
  role: AdminWorkspaceRoles;
  mutateMyTeamWorklists: () => void;
};

export const MyTeamWorklistsWidgetComponent: React.FC<Props> = (
  props: Props,
) => {
  const sxStyles = useSxStyles();
  const { worklistData, isLoading, failedToLoad, role, mutateMyTeamWorklists } =
    props;

  const widgetContentRef = useRef(null);
  const dimensions = useRefDimensions({
    ref: widgetContentRef,
  });

  const getRowMode = useCallback((width: number) => {
    if (width > 450) {
      return WorklistRowMode.Table;
    } else if (width > 300) {
      return WorklistRowMode.SemiItem;
    }
    return WorklistRowMode.Item;
  }, []);

  const rowMode = getRowMode(dimensions.width);

  const [selectedTasks, setSelectedTasks] = React.useState<
    Record<number, boolean>
  >({});

  const isAnyTaskSelected = Object.values(selectedTasks).some(
    (selected) => selected,
  );

  const handleUnselectAll = () => {
    setSelectedTasks({});
  };

  const handleUpdateTasks = React.useCallback(
    async (status: 'SKIPPED' | 'COMPLETED' | 'IN_PROGRESS') => {
      if (!worklistData) return;

      const taskIds = Object.keys(selectedTasks)
        .filter((taskId) => selectedTasks[Number(taskId)])
        .map((taskId) => Number(taskId));

      if (taskIds.length === 0) return;

      const roleValue = Object.values(People.Roles3).find(
        (r) => r.toString() === role.toString(),
      );

      if (!roleValue) {
        console.error('Invalid role:', role);
        return;
      }

      const updatePayload = {
        employeeId: 'me',
        body: new BulkUpdateWorklistTasksStatusInputDto({
          taskStatus:
            status as People.BulkUpdateWorklistTasksStatusInputDtoTaskStatus,
          taskIds: taskIds,
          roles: [roleValue],
        }),
      };

      try {
        await CollaborationReadApiClient.updateTeamWorklists(
          updatePayload.employeeId,
          updatePayload.body,
        ).then(() => {
          mutateMyTeamWorklists();
          setSelectedTasks({});
        });
      } catch (err) {
        console.error('Failed to update tasks:', err);
      }
    },
    [worklistData, selectedTasks, role, mutateMyTeamWorklists],
  );

  return (
    <>
      <AsyncContent
        isLoading={isLoading}
        failedToLoad={failedToLoad}
        customSxStyles={{ errorMessage: sxStyles.errorMessage }}
      >
        <MarkTaskActions
          onUnselectAll={handleUnselectAll}
          onUpdateTasks={handleUpdateTasks}
          hasTaskSelection={isAnyTaskSelected}
          rowMode={rowMode}
        />
        <WorklistPanels
          worklistData={worklistData}
          widgetContentRef={widgetContentRef}
          rowMode={rowMode}
          myWorklists={false}
          setSelectedTasks={setSelectedTasks}
          selectedTasks={selectedTasks}
        />
      </AsyncContent>
    </>
  );
};
